import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';

import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { environment } from '../../environments/environment';
import { UserService } from '../services/user.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly router = inject(Router);
  private readonly notification = inject(NzNotificationService);
  private readonly userService = inject(UserService);

  handleError(error: Error | HttpErrorResponse) {
    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error);
      return;
    }

    this.captureInSentry(error);
    this.logInDev(error);
    // Generic error message
    this.notification.error('Error', 'Something went wrong, please try again');
  }

  private handleHttpError(error: HttpErrorResponse): void {
    this.logInDev(error);
    switch (error.status) {
      case 401:
        this.router.navigate(['/login']);
        return;

      case 403:
        this.notification.error('Access denied', 'You do not have permission to perform this action');
        return;

      case 404:
        this.notification.warning('Not found', 'The requested resource was not found');
        return;

      case 0:
        // network error / CORS / server unreachable
        this.notification.error('Network error', 'Could not reach the server, please check your connection');
        this.captureInSentry(error);
        return;

      default:
        // 5xx and unexpected — report to Sentry
        this.captureInSentry(error);
        this.notification.error('Error', 'Something went wrong, please try again');
    }
  }
  private captureInSentry(error: Error | HttpErrorResponse): void {
    Sentry.captureException(error, {
      contexts: {
        user: { email: this.userService.username || 'unknown' },
      },
    });
  }

  private logInDev(error: Error | HttpErrorResponse): void {
    if (!environment.production) {
      console.error('Global error handler:', error);
    }
  }
}
